import React, { useState } from 'react';
import { X, Key, Terminal, Sparkles, Copy, Check, ChevronRight, ExternalLink } from 'lucide-react';

// IDE options with icons
const ideOptions = [
    { id: 'cursor', name: 'Cursor', icon: '⚡' },
    { id: 'claude', name: 'Claude Code', icon: '🤖' },
    { id: 'vscode', name: 'VS Code', icon: '💻' },
    { id: 'other', name: 'Other IDEs', icon: '🔧' },
];

export default function QuickInstallModal({ isOpen, onClose }) {
    const [selectedIde, setSelectedIde] = useState('cursor');
    const [copiedStep, setCopiedStep] = useState(null);
    const [apiKeyCreated, setApiKeyCreated] = useState(false);

    if (!isOpen) return null;

    const handleCopy = (step, text) => {
        navigator.clipboard.writeText(text);
        setCopiedStep(step);
        setTimeout(() => setCopiedStep(null), 2000);
    };

    const getInstallCommand = () => {
        switch (selectedIde) {
            case 'cursor':
                return 'cursor://pluckr/install-mcp';
            case 'claude':
                return 'npx @pluckr/mcp-server@latest';
            case 'vscode':
                return 'code --install-extension pluckr.mcp-server';
            default:
                return 'npm install -g @pluckr/mcp-cli && pluckr init';
        }
    };

    const getInstallButtonText = () => {
        switch (selectedIde) {
            case 'cursor':
                return 'Add to Cursor';
            case 'claude':
                return 'Run in Terminal';
            case 'vscode':
                return 'Install Extension';
            default:
                return 'Copy Command';
        }
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div
                    className="relative w-full max-w-lg bg-white dark:bg-[#282929] rounded-3xl shadow-[0_32px_64px_rgba(0,0,0,0.15)] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        fontFamily: "'Inter', sans-serif",
                        animation: 'modalSlideIn 0.3s ease-out'
                    }}
                >
                    {/* Header with gradient accent bar */}
                    <div className="relative">
                        <div
                            className="absolute top-0 left-0 right-0 h-1.5"
                            style={{
                                background: 'linear-gradient(90deg, #9be38f 0%, #6dd55f 50%, #4ade80 100%)'
                            }}
                        />
                        <div className="flex items-center justify-between px-8 pt-8 pb-4">
                            <div>
                                <h2
                                    className="text-2xl font-bold text-[#1a1a1a] dark:text-white"
                                    style={{ fontFamily: "'Lora', serif" }}
                                >
                                    Set Up Pluckr MCP
                                </h2>
                                <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mt-1">
                                    Connect Pluckr with your favorite IDE
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/10 transition-colors text-[#5a5a5a] dark:text-white/70 hover:text-[#1a1a1a] dark:hover:text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-8 pb-8 space-y-6 max-h-[70vh] overflow-y-auto">

                        {/* Step 1: Create API Key */}
                        <div className="rounded-2xl border-2 border-[#e8f5e3] dark:border-white/15 bg-gradient-to-br from-[#f8fdf7] to-white dark:from-[rgba(155,227,143,0.10)] dark:to-[#282929] p-5">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#9be38f] flex items-center justify-center text-[#1a1a1a] font-bold text-sm">
                                    1
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-[#1a1a1a] dark:text-white text-lg">Create an API Key</h3>
                                    <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mt-1 mb-4">
                                        Generate a secure API key to connect Pluckr to your IDE
                                    </p>
                                    <button
                                        onClick={() => setApiKeyCreated(true)}
                                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${apiKeyCreated
                                                ? 'bg-[#e8f5e3] dark:bg-[rgba(155,227,143,0.14)] text-[#2d8a2d] dark:text-[#9be38f]'
                                                : 'bg-white dark:bg-[#282929] border-2 border-[#e0e0e0] dark:border-white/15 text-[#1a1a1a] dark:text-white hover:border-[#9be38f] hover:bg-[#f8fdf7] dark:hover:bg-white/10'
                                            }`}
                                    >
                                        <Key className="w-4 h-4" />
                                        {apiKeyCreated ? 'Key Created!' : 'Create a Key'}
                                        {apiKeyCreated && <Check className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Step 2: Install MCP Setting */}
                        <div className="rounded-2xl border-2 border-[#e5e7eb] dark:border-white/15 bg-white dark:bg-[#282929] p-5">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#f0f0f0] dark:bg-white/10 flex items-center justify-center text-[#5a5a5a] dark:text-white/70 font-bold text-sm">
                                    2
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="font-semibold text-[#1a1a1a] dark:text-white text-lg">Install MCP Setting</h3>
                                        <a
                                            href="https://docs.pluckr.ai/installation"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-sm text-[#5a5a5a] dark:text-white/70 hover:text-[#9be38f] flex items-center gap-1"
                                        >
                                            View Docs <ExternalLink className="w-3 h-3" />
                                        </a>
                                    </div>

                                    {/* IDE Tabs - Custom styled for light theme */}
                                    <div className="flex rounded-xl bg-[#f5f5f5] dark:bg-[#161616] p-1 mb-4">
                                        {ideOptions.map((ide) => (
                                            <button
                                                key={ide.id}
                                                onClick={() => setSelectedIde(ide.id)}
                                                className={`flex-1 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${selectedIde === ide.id
                                                        ? 'bg-white dark:bg-[#282929] text-[#1a1a1a] dark:text-white shadow-md'
                                                        : 'text-[#5a5a5a] dark:text-[#e0e0e0] hover:text-[#1a1a1a] dark:hover:text-white'
                                                    }`}
                                            >
                                                <span className="mr-1.5">{ide.icon}</span>
                                                {ide.name}
                                            </button>
                                        ))}
                                    </div>

                                    <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mb-4">
                                        {selectedIde === 'cursor' && 'One-click to install the Pluckr MCP server in Cursor.'}
                                        {selectedIde === 'claude' && 'Run the following command to set up with Claude Code.'}
                                        {selectedIde === 'vscode' && 'Install the Pluckr extension from VS Code marketplace.'}
                                        {selectedIde === 'other' && 'Use our CLI to set up Pluckr with any IDE.'}
                                    </p>

                                    <button
                                        onClick={() => handleCopy('install', getInstallCommand())}
                                        className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all ${copiedStep === 'install'
                                                ? 'bg-[#9be38f] text-[#1a1a1a]'
                                                : 'bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]'
                                            }`}
                                    >
                                        <Terminal className="w-4 h-4" />
                                        {copiedStep === 'install' ? 'Copied!' : getInstallButtonText()}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Step 3: Start Using Pluckr */}
                        <div className="rounded-2xl border-2 border-[#e5e7eb] dark:border-white/15 bg-white dark:bg-[#282929] p-5">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#f0f0f0] dark:bg-white/10 flex items-center justify-center text-[#5a5a5a] dark:text-white/70 font-bold text-sm">
                                    3
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-[#1a1a1a] dark:text-white text-lg">Start Using Pluckr</h3>
                                    <p className="text-[#5a5a5a] dark:text-[#e0e0e0] text-sm mt-1 mb-4">
                                        Simply type the prompt below in your code editor and we'll handle the rest
                                    </p>

                                    {/* Copyable prompt - styled distinctively */}
                                    <div
                                        className="relative group rounded-xl overflow-hidden bg-gradient-to-br from-[#f8fdf7] to-[#e8f5e3] dark:from-[#161616] dark:to-[rgba(155,227,143,0.12)] border-2 border-[#d4edda] dark:border-white/15"
                                    >
                                        <div className="flex items-center justify-between px-4 py-3.5">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="w-4 h-4 text-[#9be38f]" />
                                                <code
                                                    className="text-[#1a1a1a] dark:text-white text-sm font-medium"
                                                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                                                >
                                                    Hey, help me add Pluckr payments to my project.
                                                </code>
                                            </div>
                                            <button
                                                onClick={() => handleCopy('prompt', 'Hey, help me add Pluckr payments to my project.')}
                                                className={`p-2 rounded-lg transition-all ${copiedStep === 'prompt'
                                                        ? 'bg-[#9be38f] text-[#1a1a1a]'
                                                        : 'bg-white/80 dark:bg-white/10 text-[#5a5a5a] dark:text-white/70 hover:bg-white dark:hover:bg-white/15 hover:text-[#1a1a1a] dark:hover:text-white'
                                                    }`}
                                            >
                                                {copiedStep === 'prompt' ? (
                                                    <Check className="w-4 h-4" />
                                                ) : (
                                                    <Copy className="w-4 h-4" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="px-8 pb-8">
                        <button
                            onClick={onClose}
                            className="w-full py-4 rounded-2xl font-semibold text-[#1a1a1a] transition-all relative overflow-hidden group"
                            style={{
                                background: 'linear-gradient(135deg, #9be38f 0%, #7dd87d 100%)',
                            }}
                        >
                            <span className="relative z-10 flex items-center justify-center gap-2">
                                Got it
                                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </span>
                            <div
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                style={{
                                    background: 'linear-gradient(135deg, #8ad37f 0%, #6dc86d 100%)',
                                }}
                            />
                        </button>
                    </div>

                    {/* Modal animation styles */}
                    <style>{`
            @keyframes modalSlideIn {
              from {
                opacity: 0;
                transform: translateY(20px) scale(0.98);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>
                </div>
            </div>
        </>
    );
}
